import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search, AlertCircle, CheckCircle2, Wrench, ChevronRight,
  ExternalLink, HelpCircle, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Error Codes Wiki Page
const ErrorCodesPage = () => {
  const [errorCodes, setErrorCodes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedCode, setSelectedCode] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchErrorCodes();
  }, [selectedPlatform]);

  const fetchErrorCodes = async () => {
    try {
      let url = `${API_URL}/api/errors`;
      if (selectedPlatform) {
        url += `?platform=${encodeURIComponent(selectedPlatform)}`;
      }
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setErrorCodes(data.codes || []);
        if (data.platforms) {
          setPlatforms(data.platforms);
        }
      }
    } catch (error) {
      console.error('Error fetching error codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      fetchErrorCodes();
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/errors/search/${encodeURIComponent(searchQuery)}`);
      if (res.ok) {
        const data = await res.json();
        setErrorCodes(data.results || []);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-green-100 text-green-700 border-green-200';
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'critical': return 'Crítico';
      case 'high': return 'Alto';
      case 'medium': return 'Medio';
      default: return 'Bajo';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="error-codes-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-[#0047FF]">Inicio</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">Códigos de Error</span>
          </div>
          
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <AlertCircle className="w-8 h-8 text-red-500" />
            Base de Conocimiento de Errores
          </h1>
          <p className="text-gray-600">
            Encuentra soluciones a los códigos de error más comunes de consolas
          </p>
          
          {/* Search & Filter */}
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar código de error: E74, CE-34878-0..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="error-search-input"
                />
              </div>
              <Button type="submit" className="bg-[#0047FF]">Buscar</Button>
            </form>
            
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg bg-white"
              data-testid="platform-filter"
            >
              <option value="">Todas las plataformas</option>
              {platforms.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0047FF]"></div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Error Codes List */}
            <div className="lg:col-span-2 space-y-4">
              {errorCodes.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                  <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    No se encontraron códigos de error
                  </h3>
                  <p className="text-gray-500">
                    Prueba con otro término de búsqueda
                  </p>
                </div>
              ) : (
                errorCodes.map((error) => (
                  <motion.div
                    key={error.code}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-white rounded-xl border p-6 cursor-pointer hover:shadow-lg transition-all ${
                      selectedCode?.code === error.code ? 'border-[#0047FF] ring-2 ring-[#0047FF]/20' : 'border-gray-200'
                    }`}
                    onClick={() => setSelectedCode(error)}
                    data-testid={`error-card-${error.code}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-mono text-xl font-bold text-[#0047FF]">
                            {error.code}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full border ${getSeverityColor(error.severity)}`}>
                            {getSeverityLabel(error.severity)}
                          </span>
                        </div>
                        <h3 className="font-semibold">{error.title}</h3>
                        <p className="text-sm text-gray-500">{error.platform}</p>
                      </div>
                      {error.requires_repair ? (
                        <span className="flex items-center gap-1 text-xs bg-red-50 text-red-600 px-2 py-1 rounded">
                          <Wrench className="w-3 h-3" />
                          Requiere reparación
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs bg-green-50 text-green-600 px-2 py-1 rounded">
                          <CheckCircle2 className="w-3 h-3" />
                          Auto-solucionable
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-600 text-sm">{error.description}</p>
                    
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        {error.solutions?.length || 0} soluciones disponibles
                      </span>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Detail Panel */}
            <div className="lg:col-span-1">
              {selectedCode ? (
                <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-4">
                  <div className="mb-6">
                    <span className="font-mono text-2xl font-bold text-[#0047FF]">
                      {selectedCode.code}
                    </span>
                    <h2 className="text-xl font-semibold mt-2">{selectedCode.title}</h2>
                    <p className="text-gray-500 text-sm">{selectedCode.platform}</p>
                  </div>

                  {/* Common Causes */}
                  <div className="mb-6">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-orange-500" />
                      Causas comunes
                    </h3>
                    <ul className="space-y-2">
                      {selectedCode.common_causes?.map((cause, i) => (
                        <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                          <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-2 flex-shrink-0" />
                          {cause}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Solutions */}
                  <div className="mb-6">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Soluciones
                    </h3>
                    <div className="space-y-3">
                      {selectedCode.solutions?.map((solution, i) => (
                        <div key={i} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">
                              Paso {solution.step}
                            </span>
                            <span className="text-xs text-green-600">
                              {solution.success_rate}% éxito
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{solution.action}</p>
                          <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500 rounded-full"
                              style={{ width: `${solution.success_rate}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Repair Info */}
                  {selectedCode.requires_repair && (
                    <div className="p-4 bg-red-50 rounded-lg mb-4">
                      <p className="text-sm text-red-700 font-medium mb-1">
                        Este error probablemente requiere reparación profesional
                      </p>
                      {selectedCode.estimated_repair_cost && (
                        <p className="text-sm text-red-600">
                          Coste estimado: {selectedCode.estimated_repair_cost}
                        </p>
                      )}
                    </div>
                  )}

                  {!selectedCode.requires_repair && selectedCode.estimated_self_fix_time && (
                    <p className="text-sm text-gray-500 mb-4">
                      Tiempo estimado de auto-reparación: {selectedCode.estimated_self_fix_time}
                    </p>
                  )}

                  <Link to="/warranty/new">
                    <Button className="w-full bg-[#0047FF]">
                      Solicitar Reparación
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                  <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    Selecciona un código de error para ver los detalles y soluciones
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorCodesPage;
